<script lang="ts">
  import { onMount } from 'svelte';
  import debounce from 'lodash/debounce';
  import { toCSSVars } from '$lib/utils/cssVars';

  export let gap: number;

  let containerEl: HTMLDivElement;
  let slotEl: HTMLDivElement;
  let copiesEl: HTMLDivElement;

  let animationSpeed = 20; // In px per second
  let animationTime = 0;
  let debounceResizeEventForMS = 200;

  function updateAnimationTime(fullWidthAmount: number) {
    animationTime = ((slotEl.clientWidth + gap) * fullWidthAmount) / 2 / animationSpeed;
  }

  function renderSlotCopies(fullWidthAmount: number) {
    const desiredCopiesAmount = fullWidthAmount * 2 - 1;

    let copiesHTML = '';
    for (let i = 0; i < desiredCopiesAmount; i++) {
      copiesHTML = copiesHTML.concat(slotEl.innerHTML);
    }

    copiesEl.innerHTML = copiesHTML;
  }

  function handleChange() {
    if (!containerEl || !slotEl) {
      return;
    }

    const fullWidthAmount = Math.ceil(containerEl.clientWidth / (slotEl.clientWidth + gap));

    renderSlotCopies(fullWidthAmount);
    updateAnimationTime(fullWidthAmount);
  }

  const debouncedHandleChange = debounce(handleChange, debounceResizeEventForMS);

  onMount(() => {
    handleChange();
  });
</script>

<svelte:window on:resize={debouncedHandleChange} />

<div
  bind:this={containerEl}
  class="marquee"
  style={toCSSVars({ 'items-gap': `${gap}px`, 'animation-time': `${animationTime}s` })}
>
  <div class="content">
    <div class="slot" bind:this={slotEl}>
      <slot />
    </div>
    <div class="copies" bind:this={copiesEl} aria-hidden="true" />
  </div>
</div>

<style lang="scss">
  @import 'src/scss/accessibility';

  .marquee {
    max-width: 100%;
    overflow: hidden;
  }

  .content {
    display: flex;
    align-items: center;
    width: fit-content;
    gap: var(--items-gap);
    animation: marquee var(--animation-time) linear infinite;

    &:hover {
      animation-play-state: paused;
    }
  }

  .slot {
    display: inline-block;
  }

  .copies {
    display: flex;
    align-items: center;
    gap: var(--items-gap);
  }

  @keyframes marquee {
    0% {
      transform: translate3d(0, 0, 0);
    }
    100% {
      transform: translate3d(calc(-50% - var(--items-gap) / 2), 0, 0);
    }
  }

  @include prefers-reduce-motion {
    .content {
      animation: none;
    }
  }
</style>
